import { useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { User, Mail, Crown, Calendar, Shield, Lock, Save, Loader2 } from "lucide-react";

const Profile = () => {
  const { user, token, checkAuth } = useAuth();

  const roleLabels = {
    student: { label: "Étudiant", color: "bg-blue-500/20 text-blue-400" },
    instructor: { label: "Instructeur", color: "bg-purple-500/20 text-purple-400" },
    admin: { label: "Administrateur", color: "bg-red-500/20 text-red-400" },
  };

  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [saving, setSaving] = useState(false);

  const dirty =
    form.name !== (user?.name || "") ||
    form.email !== (user?.email || "") ||
    form.new_password.length > 0;

  const handleSave = async () => {
    if (form.new_password && form.new_password !== form.confirm_password) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    if (form.new_password && !form.current_password) {
      toast.error("Saisis ton mot de passe actuel pour le changer");
      return;
    }

    setSaving(true);
    try {
      const payload = { name: form.name, email: form.email };
      if (form.new_password) {
        payload.current_password = form.current_password;
        payload.new_password = form.new_password;
      }
      const response = await axios.put(`${API}/auth/me`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.token) {
        localStorage.setItem("token", response.data.token);
      }
      await checkAuth();
      setForm((f) => ({ ...f, current_password: "", new_password: "", confirm_password: "" }));
      toast.success("Profil mis à jour");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="profile-page">
      <Navbar />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-heading font-bold mb-8">Mon Profil</h1>

        {/* Profile Card */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary/20 to-secondary/20 p-6">
            <div className="flex items-center gap-4">
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-20 h-20 rounded-full border-4 border-background"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-primary/20 border-4 border-background flex items-center justify-center">
                  <User className="w-10 h-10 text-primary" />
                </div>
              )}
              <div>
                <h2 className="text-2xl font-heading font-bold">{user?.name}</h2>
                <Badge className={roleLabels[user?.role || "student"].color}>
                  {roleLabels[user?.role || "student"].label}
                </Badge>
              </div>
            </div>
          </div>

          {/* Details (éditables) */}
          <div className="p-6 space-y-6">
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Mail className="w-4 h-4" />
                Email
              </Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="votre@email.com"
              />
            </div>

            <div>
              <Label className="flex items-center gap-2 mb-2">
                <User className="w-4 h-4" />
                Nom complet
              </Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Votre nom"
              />
            </div>

            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4" />
                ID Utilisateur
              </Label>
              <Input value={user?.user_id || ""} disabled className="font-mono text-sm" />
            </div>
          </div>
        </div>

        {/* Changement de mot de passe */}
        <div className="mt-8 bg-card border border-border rounded-lg p-6">
          <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Changer le mot de passe
          </h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label className="mb-2 block">Mot de passe actuel</Label>
              <Input
                type="password"
                value={form.current_password}
                onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                placeholder="••••••••"
              />
            </div>
            <div>
              <Label className="mb-2 block">Nouveau mot de passe</Label>
              <Input
                type="password"
                value={form.new_password}
                onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                placeholder="Min. 6 caractères"
              />
            </div>
            <div>
              <Label className="mb-2 block">Confirmer</Label>
              <Input
                type="password"
                value={form.confirm_password}
                onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                placeholder="••••••••"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Laisse ces champs vides si tu ne veux pas changer ton mot de passe.
          </p>
        </div>

        {/* Bouton enregistrer */}
        <div className="mt-6 flex justify-end">
          <Button onClick={handleSave} disabled={saving || !dirty} className="glow-green">
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enregistrement...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" /> Enregistrer les modifications</>
            )}
          </Button>
        </div>

        {/* Account Settings */}
        <div className="mt-8 bg-card border border-border rounded-lg p-6">
          <h3 className="font-heading font-bold text-lg mb-4">Paramètres du compte</h3>
          <div className="space-y-4">
            <Button variant="outline" className="w-full justify-start">
              <Crown className="w-4 h-4 mr-2" />
              Gérer mon abonnement
            </Button>
            <Button variant="outline" className="w-full justify-start">
              <Calendar className="w-4 h-4 mr-2" />
              Historique des paiements
            </Button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Profile;
